import { Check, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { VendorPicker } from '@/features/quotes/components/VendorPicker'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import type { CostLine } from '@/types/domain'

/* The inline row: a table row that IS the form, for adding AND for editing.
 *
 * The PRD's counter-signal for this surface is that quoting must not be slower
 * than the Excel path it replaces, and the cost editor's own header has said
 * since it shipped that "adding never opens a modal from a button hunt". It did
 * anyway — the add button opened the same sheet as editing. This is that row.
 *
 * Three things make it faster than the sheet, and each is a deliberate cost:
 *
 *   - **It never leaves.** In ADD mode, saving clears the fields and returns
 *     focus to the first one, so ten lines are ten uninterrupted passes. There
 *     is no reopen.
 *   - **Enter saves, Escape cancels.** Both from any field, so a hand that
 *     never leaves the keyboard can finish a line. Tab moves between fields
 *     normally; it is not overloaded to save.
 *   - **It validates on SAVE, not on keystroke.** A row going red while a
 *     number is half-typed is noise at this speed. An invalid save marks the
 *     offending fields and keeps everything typed.
 *
 * EDIT mode is the same row seeded from a line, and it differs in exactly two
 * places, both because one considered change is not a burst of many: saving
 * closes the row (`onSaved` calls the caller's exit rather than clearing), and
 * blur does not save. A stray click off a row you were only reading must not
 * write, and the field-to-field blur guard cannot tell reading from typing once
 * the row already holds values.
 *
 * Phone gets no inline row at all. A horizontally scrolling editable grid is
 * prohibited on this surface, so the sheet remains the phone's path for both.
 *
 * ONE money field, and it is the unit COST — the same single price the sheet
 * asks for, under the same heading, because two forms writing the same line
 * must not disagree about which side of it they collect. The customer's price
 * is derived from this figure by the markup panel and is never typed here; see
 * `toCostLinePayload`, which sends what is typed as both sides of the line.
 *
 * TWO money CELLS, though, because the table has two money columns: the unit
 * price is typed and the amount beside it is the extension, shown live while
 * the row is still being filled. It is display arithmetic on a value already in
 * hand — the same product the read row prints — and it is here rather than
 * only in the read row because the quantity is the field an operator notices
 * last. Typing 120.00 at quantity 3 and seeing 120.00 until the row is saved
 * means the mistake is found one line too late. Nothing about that cell reaches
 * the payload: the row still sends the strings that were typed (AD-9).
 */

export interface CostLineInlineDraft {
  description: string
  vendorName: string
  /** The directory row the vendor name resolves to, or null when it was typed
   *  (migration 0044). Derived by VendorPicker from the name, never entered. */
  vendorId: string | null
  quantity: string
  /** The unit COST — the one price this table collects, as in the workbook.
   *  Structurally the sheet's `CostLineDraft`, which is what lets one payload
   *  function serve both forms. */
  unitPrice: string
}

const emptyInlineDraft: CostLineInlineDraft = {
  description: '',
  vendorName: '',
  vendorId: null,
  quantity: '1',
  unitPrice: '',
}

/* The same rules the sheet applies, duplicated here rather than imported from
   it: the sheet owns a whole form's worth of state and lifecycle, and importing
   its validators would couple the fast path to the slow one. They are four
   lines and they must agree — a divergence shows up as a row the server
   refuses.

   The parse is split out from the verdict because the amount cell needs the
   FIGURE as well as the yes/no: it prints the extension of a price that is
   still being typed. A second parser beside this one would be a second set of
   rules about commas and thin spaces, and the first time they disagreed the row
   would show an amount the save then refused — the worst possible pairing,
   because the cell that is meant to catch the mistake would be the one making
   it. One parser, two readers. */
function parseAmount(raw: string): number | null {
  const normalised = raw.replace(/\s/g, '').replace(',', '.')
  if (normalised === '' || !/^\d+(\.\d{1,2})?$/.test(normalised)) return null
  return Number(normalised)
}

function isValidAmount(raw: string): boolean {
  return parseAmount(raw) !== null
}

function isPositiveInteger(raw: string): boolean {
  return /^\d+$/.test(raw.trim()) && Number(raw) > 0
}

/** Bare cell input: no shell and no fill at rest. The ROW is the control here —
 *  a five-box control set inside a data row would be taller than the rows above
 *  it and would read as a different table.
 *
 *  Focus is announced by a BORDER, matching `controlClass` in ui/control.ts, and
 *  the global `--focus-ring` halo is cancelled with `focus:shadow-none`. The
 *  halo bled a 3px purple glow past the cell into its neighbours, which at this
 *  row height reads as a smeared box rather than a focused field.
 *
 *  The transparent border and the padding are carried at REST as well as on
 *  focus. If they only appeared on focus, the text would jump a pixel each time
 *  the field was entered, which on a row filled by ten fast tab presses is the
 *  most visible thing on the screen. */
const CELL =
  'w-full rounded-[5px] border border-transparent bg-transparent px-1.5 py-0.5 ' +
  'text-[13px] text-text-heading placeholder:text-text-muted/60 ' +
  'outline-none focus:border-brand focus:outline-none focus:shadow-none'

export function CostLineInlineRow({
  record,
  seedUnitPrice,
  onSubmit,
  onCancel,
  pending,
}: {
  /** The line being edited. Absent means this is the add row. */
  record?: CostLine
  /** A unit price to open on INSTEAD of the line's own: the FR-17 rate memory,
   *  accepted by a tap on the chip in the read row above.
   *
   *  It seeds the field and nothing else — the figure is on screen, in an input,
   *  under the hand that asked for it, and it reaches the server only when the
   *  row is saved. That is the whole distinction FR-17 is written around: a hint
   *  REPORTS what the lane cost last time, it does not agree to it. */
  seedUnitPrice?: string
  /** Saves the row. The caller clears it by resolving `onSaved`. */
  onSubmit: (draft: CostLineInlineDraft, onSaved: () => void) => void
  onCancel: () => void
  pending?: boolean
}) {
  const { t } = useTranslation()

  const editing = record !== undefined

  /* The vendor is absent from a Sales user's DTO (FR-29, AD-6). An absent
     value edits as empty and the field locks, exactly as the sheet does it —
     typing over a value you cannot see would overwrite somebody else's. */
  const vendorHidden =
    record?.vendorName !== undefined && typeof record.vendorName !== 'string'

  const [draft, setDraft] = useState<CostLineInlineDraft>(() =>
    record
      ? {
          description: record.description,
          vendorName:
            typeof record.vendorName === 'string' ? record.vendorName : '',
          /* Suppressed and absent are the same shape here on purpose — see the
             server's CostLineDTO.VendorID. Either way there is no counterparty
             this row may reopen on, and the field is locked below anyway. */
          vendorId: typeof record.vendorId === 'string' ? record.vendorId || null : null,
          quantity: String(record.quantity),
          /* The COST, never the sell. On a line that has been through a markup
             the two differ, and seeding from the sell would write the
             marked-up figure back as the cost the next markup multiplies —
             a quote that climbs by one markup every time a description is
             corrected. */
          unitPrice: seedUnitPrice ?? record.buy.amount,
        }
      : { ...emptyInlineDraft, unitPrice: seedUnitPrice ?? '' },
  )
  const [invalid, setInvalid] = useState(false)
  const firstFieldRef = useRef<HTMLInputElement>(null)
  /* Set when a save succeeds, cleared once focus has actually been placed. It
     exists because the moment we WANT to focus and the moment we CAN are not
     the same render — see the effect below. */
  const refocusPending = useRef(false)

  /* Focus the row the moment it appears: it was summoned by a click on Add or
     on the line itself, so the next keystroke is meant for it. The effect runs
     once on mount because the row is mounted and unmounted by the parent rather
     than toggled.
     Editing selects what is there, so the first keystroke replaces the old
     value instead of appending to it — the field was opened on a value the user
     can already read, and retyping it is the common case. */
  useEffect(() => {
    firstFieldRef.current?.focus()
    if (editing) firstFieldRef.current?.select()
    // Mount only; `editing` cannot change for a given mounted row.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* Return focus to the first field once the write has finished, NOT when it
     succeeds. `onSaved` runs from the mutation's `onSuccess`, and on that render
     `pending` is still true — so the inputs are still `disabled`, and focusing a
     disabled input is a silent no-op that drops focus to <body>. The next line
     typed then goes nowhere, which is the whole point of the row.
     Waiting for `pending` to clear focuses the field on the render where it is
     interactive again. */
  useEffect(() => {
    if (pending || !refocusPending.current) return
    refocusPending.current = false
    firstFieldRef.current?.focus()
  }, [pending])

  const descriptionInvalid = invalid && !draft.description.trim()
  const quantityInvalid = invalid && !isPositiveInteger(draft.quantity)
  const unitPriceInvalid = invalid && !isValidAmount(draft.unitPrice)

  /* The live extension for the amount cell. `null` while either field is blank
     or half-typed, and the cell shows the empty marker for it rather than a
     zero: 0.00 is a figure, and a cost line reading 0.00 while its price is
     still under the hand is the line an operator tabs past.
     Number arithmetic, deliberately, and only here — this figure is READ. It is
     the same concession `formatNumber` and the read row's `extended` make; the
     draft still travels to the server as the strings that were typed (AD-9). */
  const unit = parseAmount(draft.unitPrice)
  const qty = isPositiveInteger(draft.quantity) ? Number(draft.quantity) : null
  const extension = unit !== null && qty !== null ? unit * qty : null

  const save = () => {
    if (pending) return
    const ok =
      draft.description.trim() !== '' &&
      isPositiveInteger(draft.quantity) &&
      isValidAmount(draft.unitPrice)
    if (!ok) {
      setInvalid(true)
      return
    }
    onSubmit(draft, () => {
      /* Adding: cleared and refocused rather than closed, because the next line
         is the likely next action and reopening the row would cost a click per
         line. The focus itself is deferred to the effect above — here the
         inputs are still disabled by `pending`, so focusing now would do
         nothing.
         Editing: one considered change is done when it is saved, so the row
         hands itself back to the caller and becomes a read row again. */
      if (editing) {
        onCancel()
        return
      }
      setDraft(emptyInlineDraft)
      setInvalid(false)
      refocusPending.current = true
    })
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    /* Enter and Shift+Enter both commit the line and leave a blank row focused,
       because the row never closes on save — the difference is where the hand
       already is. Enter is the one-line reflex; Shift+Enter is the "and another"
       that grids elsewhere have trained, and a user who reaches for it must not
       get a newline or a submitted page instead.
       Both are declared here rather than one falling through to the other so
       that neither can quietly become the browser's default. */
    if (event.key === 'Enter') {
      event.preventDefault()
      save()
      return
    }
    if (event.key === 'Escape') {
      /* One Escape, two things it could mean, and the row must not take the
         other one's. The vendor cell is a combobox (0044): while its suggestion
         list is open, Escape closes the LIST — and a row that also cancelled
         would throw away a half-typed line because somebody dismissed a
         dropdown. The expanded state is read off the event's own target rather
         than tracked here, so this row keeps holding no opinion about how the
         picker works. */
      const target = event.target as HTMLElement | null
      if (target?.getAttribute('aria-expanded') === 'true') return

      event.preventDefault()
      onCancel()
    }
  }

  return (
    <tr
      className="border-line-hair bg-surface-hover/40 h-[38px] border-b"
      onKeyDown={onKeyDown}
    >
      {/* Cell padding is pulled back by the input's own 6px so the typed text
          still sits on the same left edge as the rows above — the padding moved
          inside the field, it was not added to the row. */}
      <td className="px-3">
        <input
          ref={firstFieldRef}
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          placeholder={t('quotes.costLines.description')}
          aria-label={t('quotes.costLines.description')}
          aria-invalid={descriptionInvalid || undefined}
          disabled={pending}
          className={cn(CELL, descriptionInvalid && 'text-negative')}
        />
      </td>
      <td className="px-1.5">
        {/* `bare`, because the row already draws the grid: a bordered control
            per column turns a costing into a spreadsheet, which is the
            aesthetic this table was built away from. The picker still shows its
            focus border, so it reads as editable the moment it is entered. */}
        <VendorPicker
          value={draft.vendorName}
          onChange={(vendorName, vendorId) =>
            setDraft({ ...draft, vendorName, vendorId })
          }
          placeholder={vendorHidden ? '' : t('quotes.costLines.vendor')}
          aria-label={t('quotes.costLines.vendor')}
          disabled={pending || vendorHidden}
          bare
          className="text-text-muted text-[12.5px]"
        />
      </td>
      <td className="px-1.5">
        <input
          inputMode="numeric"
          value={draft.quantity}
          onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
          aria-label={t('quotes.costLines.quantity')}
          aria-invalid={quantityInvalid || undefined}
          disabled={pending}
          className={cn(CELL, 'asa-num text-right', quantityInvalid && 'text-negative')}
        />
      </td>
      <td className="px-3">
        <input
          inputMode="decimal"
          value={draft.unitPrice}
          onChange={(e) => setDraft({ ...draft, unitPrice: e.target.value })}
          placeholder={t('quotes.costLines.unitPrice')}
          aria-label={t('quotes.costLines.unitPrice')}
          aria-invalid={unitPriceInvalid || undefined}
          disabled={pending}
          onBlur={(event) => {
            /* ADD only. Blur saves only when focus LEAVES the row — moving
               between the row's own fields must not fire a save. A blur into
               nothing (clicking the page background) counts as leaving.
               Editing never saves on blur: the row opens already full, so a
               click elsewhere by somebody who was only reading is
               indistinguishable from one by somebody who typed. Edits commit on
               Enter or on the tick. */
            if (editing) return
            const next = event.relatedTarget as Node | null
            if (next && event.currentTarget.closest('tr')?.contains(next)) return
            if (draft.description.trim() || draft.unitPrice.trim()) save()
          }}
          className={cn(CELL, 'asa-num text-right', unitPriceInvalid && 'text-negative')}
        />
      </td>

      {/* The amount, read-only, in the column the read rows put it in. Muted
          and without the input's padding or focus border so the eye can tell at
          a glance which of the two money cells accepts a keystroke — an open
          row with two identical-looking money boxes invites the operator to
          type the extension into the one that computes it. */}
      <td className="asa-num text-text-muted px-3 text-right text-[13px]">
        {extension === null ? t('common.empty') : formatNumber(extension, 2)}
      </td>

      {/* The actions cell. It mirrors the read row's own trailing cell so the
          table keeps one column count in both states, and it carries the
          pointer path for hands that are not on the keyboard: the tick is what
          Enter does, the cross is what Escape does. */}
      <td className="px-3">
        <div className="flex items-center justify-end gap-0.5">
          <button
            type="button"
            onClick={save}
            disabled={pending}
            aria-label={t('common.actions.save')}
            title={t('common.actions.save')}
            className="text-brand hover:bg-surface-hover cursor-pointer rounded-[5px] p-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Check size={15} aria-hidden />
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            aria-label={t('common.actions.cancel')}
            title={t('common.actions.cancel')}
            className="text-text-muted hover:bg-surface-hover cursor-pointer rounded-[5px] p-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X size={15} aria-hidden />
          </button>
        </div>
      </td>
    </tr>
  )
}
